import type {
  FilterCombinator,
  FilterFieldDef,
  FilterGroup,
  FilterNode,
  FilterRule,
} from '@/types/filters';
import { MULTI_VALUE_OPERATORS, VALUELESS_OPERATORS } from '@/types/filters';

/**
 * Pure filter-tree machinery: builders, (de)serialization, pruning, evaluation
 * and immutable tree edits. No React, no I/O; safe to unit-test directly and to
 * import from anywhere (pages, the {@link useAdvancedFilters} hook, and Redux
 * selectors alike).
 *
 * NOTE: not to be confused with `features/list/_shared/domain/filter-tree.ts`,
 * which is a deliberate re-implementation for the AddList/EditList reducers: it
 * threads an explicit, serialisable id counter so transitions stay replayable
 * inside a reducer. This module instead uses a module-level mutable counter
 * (`nextId`), which is illegal in a reducer but fine for the hook. Same
 * behaviour, different id source.
 */

// ── Stable ID generation via closure (avoids module-level mutable state leaking) ──

const _nodeId = { current: 0 };
function nextId() {
  return `fn-${++_nodeId.current}`;
}

export function createEmptyRule(): FilterRule {
  return {
    id: nextId(),
    kind: 'rule',
    combinator: 'and',
    field: '',
    operator: 'contains',
    value: '',
  };
}

export function createEmptyGroup(combinator: FilterCombinator = 'and'): FilterGroup {
  return { id: nextId(), kind: 'group', combinator, children: [] };
}

// ── JSON export / import helpers ──

/** Strip internal `id` fields for a clean, portable JSON export. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function stripIds(group: FilterGroup): Record<string, any> {
  return {
    kind: group.kind,
    combinator: group.combinator,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    children: group.children.map((child): Record<string, any> => {
      if (child.kind === 'rule') {
        const { id: _, ...rest } = child;
        return rest;
      }
      return stripIds(child);
    }),
  };
}

/** Re-generate unique `id` fields on an imported filter tree. */
export function rehydrateIds(raw: Record<string, unknown>): FilterGroup {
  if (raw.kind !== 'group' || !Array.isArray(raw.children)) {
    throw new Error('Invalid filter format: root must be a group with children');
  }
  return {
    id: nextId(),
    kind: 'group',
    combinator: (raw.combinator === 'or' ? 'or' : 'and') as FilterCombinator,
    children: (raw.children as Record<string, unknown>[]).map((child): FilterNode => {
      if (child.kind === 'rule') {
        return {
          id: nextId(),
          kind: 'rule',
          combinator: (child.combinator === 'or' ? 'or' : 'and') as FilterCombinator,
          field: String(child.field ?? ''),
          operator: String(child.operator ?? 'contains') as FilterRule['operator'],
          value: String(child.value ?? ''),
          ...(Array.isArray(child.values) ? { values: child.values.map(String) } : {}),
          ...(child.valueTo != null ? { valueTo: String(child.valueTo) } : {}),
          ...(child.yearValue != null ? { yearValue: String(child.yearValue) } : {}),
          ...(child.yearValueTo != null ? { yearValueTo: String(child.yearValueTo) } : {}),
          ...(child.durationValue != null ? { durationValue: String(child.durationValue) } : {}),
          ...(child.durationValueTo != null
            ? { durationValueTo: String(child.durationValueTo) }
            : {}),
        };
      }
      if (child.kind === 'group') {
        return rehydrateIds(child);
      }
      throw new Error(`Invalid filter node kind: ${String(child.kind)}`);
    }),
  };
}

// ── Rule helpers ──

export function isRuleActive(r: FilterRule): boolean {
  if (!r.field || !r.operator) return false;
  if (VALUELESS_OPERATORS.includes(r.operator)) return true;
  if (MULTI_VALUE_OPERATORS.includes(r.operator)) return (r.values?.length ?? 0) > 0;
  return r.value.trim() !== '';
}

// ── Evaluate a single rule against a row ──

function evaluateRule(
  row: Record<string, unknown>,
  rule: FilterRule,
  fieldDefs: FilterFieldDef[],
): boolean {
  const def = fieldDefs.find((f) => f.field === rule.field);
  if (!def) return true;

  const raw = row[rule.field];

  // Array fields (e.g. types): match against array elements
  if (Array.isArray(raw)) {
    const arr = raw as string[];
    switch (rule.operator) {
      case 'contains': {
        const needle = rule.value.toLowerCase();
        return arr.some((v) => v.toLowerCase().includes(needle));
      }
      case 'not_contains': {
        const needle = rule.value.toLowerCase();
        return !arr.some((v) => v.toLowerCase().includes(needle));
      }
      case 'is_empty':
        return arr.length === 0;
      case 'is_not_empty':
        return arr.length > 0;
      case 'is_one_of': {
        const needles = (rule.values ?? []).map((v) => v.toLowerCase());
        return arr.some((v) => needles.includes(v.toLowerCase()));
      }
      case 'is_none_of': {
        const needles = (rule.values ?? []).map((v) => v.toLowerCase());
        return !arr.some((v) => needles.includes(v.toLowerCase()));
      }
      default:
        return true;
    }
  }

  switch (def.type) {
    case 'text': {
      const val = (raw as string | null | undefined) ?? '';
      const needle = rule.value.toLowerCase();
      switch (rule.operator) {
        case 'contains':
          return val.toLowerCase().includes(needle);
        case 'not_contains':
          return !val.toLowerCase().includes(needle);
        case 'equals':
          return val.toLowerCase() === needle;
        case 'not_equals':
          return val.toLowerCase() !== needle;
        case 'is_empty':
          return val.trim() === '';
        case 'is_not_empty':
          return val.trim() !== '';
        case 'is_one_of': {
          const needles = (rule.values ?? []).map((v) => v.toLowerCase());
          return needles.includes(val.toLowerCase());
        }
        case 'is_none_of': {
          const needles = (rule.values ?? []).map((v) => v.toLowerCase());
          return !needles.includes(val.toLowerCase());
        }
        case 'contains_one_of': {
          const needles = (rule.values ?? []).map((v) => v.toLowerCase());
          return needles.some((n) => val.toLowerCase().includes(n));
        }
        default:
          return true;
      }
    }

    case 'number': {
      const num = Number(raw) || 0;
      const target = Number(rule.value) || 0;
      switch (rule.operator) {
        case 'equals':
          return num === target;
        case 'not_equals':
          return num !== target;
        case 'gt':
          return num > target;
        case 'gte':
          return num >= target;
        case 'lt':
          return num < target;
        case 'lte':
          return num <= target;
        case 'between': {
          const to = Number(rule.valueTo) || 0;
          return num >= target && num <= to;
        }
        default:
          return true;
      }
    }

    case 'boolean': {
      const bool = !!raw;
      switch (rule.operator) {
        case 'is_true':
          return bool === true;
        case 'is_false':
          return bool === false;
        default:
          return true;
      }
    }

    case 'date': {
      const dateVal = raw ? new Date(raw as string).getTime() : 0;
      const target = rule.value ? new Date(rule.value).getTime() : 0;
      switch (rule.operator) {
        case 'before':
          return dateVal < target;
        case 'after':
          return dateVal > target;
        case 'between': {
          const to = rule.valueTo ? new Date(rule.valueTo).getTime() : 0;
          return dateVal >= target && dateVal <= to;
        }
        default:
          return true;
      }
    }

    default:
      return true;
  }
}

// ── Pre-compute: strip inactive nodes from tree (done once per config change, NOT per row) ──

export function pruneInactiveNodes(group: FilterGroup): FilterGroup {
  const prunedChildren: FilterNode[] = [];
  for (const child of group.children) {
    if (child.kind === 'rule') {
      if (isRuleActive(child)) prunedChildren.push(child);
    } else {
      const prunedChild = pruneInactiveNodes(child);
      if (prunedChild.children.length > 0) prunedChildren.push(prunedChild);
    }
  }
  return { ...group, children: prunedChildren };
}

// ── Drop rules bound to a field the app no longer knows ──

/**
 * Removes every rule whose field is absent from `fieldDefs`, plus every group
 * left empty by that removal. Applied to trees coming from outside the current
 * code (localStorage and saved views) because a field can be retired between
 * two releases while a user's saved rule still names it.
 *
 * Without this, such a rule renders with an empty field selector and no
 * operators, still counts towards the active-rule badge and is still sent to
 * the server: a filter that narrows the results while being invisible.
 *
 * A rule with no field yet (a row the user just added) is kept; it is not
 * unknown, it is unfinished.
 */
export function dropUnknownFields(group: FilterGroup, fieldDefs: FilterFieldDef[]): FilterGroup {
  const known = new Set(fieldDefs.map((f) => f.field));

  const walk = (node: FilterGroup): FilterGroup => {
    const children: FilterNode[] = [];
    for (const child of node.children) {
      if (child.kind === 'rule') {
        if (child.field === '' || known.has(child.field)) children.push(child);
      } else {
        const kept = walk(child);
        if (kept.children.length > 0) children.push(kept);
      }
    }
    return { ...node, children };
  };

  return walk(group);
}

// ── Evaluate a pre-pruned group against a row (no activeChildren filter needed) ──

export function evaluateGroup(
  row: Record<string, unknown>,
  group: FilterGroup,
  fieldDefs: FilterFieldDef[],
): boolean {
  if (group.children.length === 0) return true;

  let result = false;
  for (let i = 0; i < group.children.length; i++) {
    const child = group.children[i];
    const childResult =
      child.kind === 'rule'
        ? evaluateRule(row, child, fieldDefs)
        : evaluateGroup(row, child, fieldDefs);

    if (i === 0) {
      result = childResult;
    } else {
      const combinator = child.combinator;
      result = combinator === 'and' ? result && childResult : result || childResult;
    }
  }

  return result;
}

// ── Count active rules recursively ──

export function countActiveRules(group: FilterGroup): number {
  return group.children.reduce((sum, child) => {
    if (child.kind === 'rule') return sum + (isRuleActive(child) ? 1 : 0);
    return sum + countActiveRules(child);
  }, 0);
}

// ── Immutable deep tree edits (used by the hook) ──

export function updateNodeInTree(
  children: FilterNode[],
  nodeId: string,
  updater: (node: FilterNode) => FilterNode,
): FilterNode[] {
  return children.map((child) => {
    if (child.id === nodeId) return updater(child);
    if (child.kind === 'group') {
      return { ...child, children: updateNodeInTree(child.children, nodeId, updater) };
    }
    return child;
  });
}

export function removeNodeFromTree(children: FilterNode[], nodeId: string): FilterNode[] {
  return children
    .filter((child) => child.id !== nodeId)
    .map((child) => {
      if (child.kind === 'group') {
        return { ...child, children: removeNodeFromTree(child.children, nodeId) };
      }
      return child;
    });
}

export function addNodeToParent(
  children: FilterNode[],
  parentId: string,
  newNode: FilterNode,
): FilterNode[] {
  return children.map((child) => {
    if (child.id === parentId && child.kind === 'group') {
      return { ...child, children: [...child.children, newNode] };
    }
    if (child.kind === 'group') {
      return { ...child, children: addNodeToParent(child.children, parentId, newNode) };
    }
    return child;
  });
}

export function insertNodeAt(
  children: FilterNode[],
  parentId: string,
  newNode: FilterNode,
  index: number,
  rootId: string,
): FilterNode[] {
  // If parentId matches the root, insert directly
  if (parentId === rootId) {
    const copy = [...children];
    copy.splice(index, 0, newNode);
    return copy;
  }
  return children.map((child) => {
    if (child.id === parentId && child.kind === 'group') {
      const copy = [...child.children];
      copy.splice(index, 0, newNode);
      return { ...child, children: copy };
    }
    if (child.kind === 'group') {
      return { ...child, children: insertNodeAt(child.children, parentId, newNode, index, rootId) };
    }
    return child;
  });
}

export function findNodeById(children: FilterNode[], nodeId: string): FilterNode | null {
  for (const child of children) {
    if (child.id === nodeId) return child;
    if (child.kind === 'group') {
      const found = findNodeById(child.children, nodeId);
      if (found) return found;
    }
  }
  return null;
}
